import { Theme, alpha } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { FinanceGranularity } from '../../../services/financeReportsService';

/**
 * Shared chart chrome — colour ramps, formatters, defaults. Kept in one file
 * so every finance chart looks like it came from the same designer. When we
 * add the revenue series later, its colour slots in here.
 */

// Semantic colour tokens driven off the MUI theme so light/dark mode both
// look intentional. Order matters — the palette is stable across charts, so
// "expenses" is always the same tone in every view.
//
// Money-in tones read cool → warm (green/blue), money-out reads red — the
// three trend lines are then maximally distinguishable at a glance.
export const financePalette = (theme: Theme) => ({
  fees: theme.palette.primary.main,
  expenses: theme.palette.error.main,
  expensesFill: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.3 : 0.18),
  fines: theme.palette.warning.main,
  finesFill: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.3 : 0.18),
  outstanding: theme.palette.warning.main,
  revenue: theme.palette.success.main,
  approved: theme.palette.success.main,
  pending: theme.palette.warning.main,
  rejected: theme.palette.error.main,
  neutral: theme.palette.text.secondary,
  gridStroke: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.35 : 0.5),
  axisTick: theme.palette.text.secondary,
});

// Rotating palette for category / method donuts. Chosen so adjacent slices
// have adequate contrast without looking like a rainbow.
export const categoricalColors = (theme: Theme): string[] => [
  theme.palette.primary.main,
  theme.palette.secondary.main,
  theme.palette.warning.main,
  theme.palette.info.main,
  theme.palette.success.main,
  theme.palette.error.main,
  alpha(theme.palette.primary.main, 0.55),
  alpha(theme.palette.secondary.main, 0.55),
  alpha(theme.palette.warning.main, 0.55),
  alpha(theme.palette.info.main, 0.55),
];

// Currency label prefixes every amount. Passed in explicitly so this file
// stays hook-free and can be used in non-component code. See `useAuth()`
// for the source of truth (school settings → currency).
export const formatMoney = (n: number | undefined | null, currency = 'PKR'): string => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 100_000) return `${currency} ${(v / 100_000).toFixed(1)}L`;
  if (Math.abs(v) >= 1_000) return `${currency} ${(v / 1_000).toFixed(1)}K`;
  return `${currency} ${v.toLocaleString()}`;
};

export const formatMoneyFull = (n: number | undefined | null, currency = 'PKR'): string =>
  `${currency} ${Number(n || 0).toLocaleString()}`;

export const formatCount = (n: number | undefined | null): string =>
  Number(n || 0).toLocaleString();

// Bucket key formatter — picks a compact label per granularity. Used on X
// axes and tooltip titles.
export const formatBucket = (iso: string, granularity: FinanceGranularity): string => {
  try {
    const d = parseISO(iso);
    if (granularity === 'day') return format(d, 'MMM d');
    if (granularity === 'week') return format(d, "'W'w MMM d");
    if (granularity === 'month') return format(d, 'MMM yyyy');
    if (granularity === 'quarter') return `Q${Math.floor(d.getMonth() / 3) + 1} ${format(d, 'yyyy')}`;
    if (granularity === 'year') return format(d, 'yyyy');
    return format(d, 'MMM d');
  } catch {
    return iso;
  }
};

// Human status labels used across charts.
export const humanStatus = (raw: string): string => {
  const s = String(raw || '').toLowerCase();
  if (s === 'pending') return 'Pending';
  if (s === 'approved') return 'Approved';
  if (s === 'rejected') return 'Rejected';
  return raw;
};

